/**
 * `useEditorKeyboardShortcuts` — single `onKeyDownCapture` handler for
 * the editor that wires up:
 *
 *   ⌘⇧V / Ctrl⇧V  → mark next paste as plain-text (consumed by clipboard hook)
 *   ⌘F  / Ctrl F  → open Find & Replace dialog
 *   ⌘J  / Ctrl J  → toggle the AI prompt menu (gated by `enableAI`)
 *   Tab           → indent inside table cells / list items; bubble otherwise
 *   Shift+Tab     → outdent inside the same contexts
 *   Enter         → schedule a clearMarksAtCaret on next microtask so the
 *                   new line starts unformatted (Google Docs behavior)
 *
 * Why a "capture" phase listener
 *   Plate's own keydown handlers fire on the bubble phase. By capturing we
 *   intercept the shortcut BEFORE Plate's defaults run, so e.g. ⌘F doesn't
 *   trigger Plate's own selection extend, and Tab doesn't fall through to
 *   the browser's focus-cycle.
 *
 * Why this hook takes so many params
 *   Each shortcut hands off to a different subsystem:
 *     - markNextPasteAsPlain → clipboard hook
 *     - setFindReplaceOpen   → React state in the shell
 *     - aiMenuRef.toggle()   → imperative ref to the AIMenu component
 *     - clearMarksAtCaret    → mark-reset hook
 *   We deliberately keep this hook as a thin orchestration layer that
 *   doesn't own any of those subsystems itself — the shell wires them up.
 */
import { useCallback } from 'react';
import { indent as plateIndent, outdent as plateOutdent } from '@platejs/indent';
import type { AIMenuHandle } from '../ai/AIMenu';

export interface UseEditorKeyboardShortcutsConfig {
    editor: any;
    readOnly: boolean;
    enableAI: boolean;
    /** From `useClipboardHandlers`. Flags the next paste as plain-text on ⌘⇧V. */
    markNextPasteAsPlain: () => void;
    /** Shell-owned state setter — opens the Find & Replace dialog on ⌘F. */
    setFindReplaceOpen: (open: boolean) => void;
    /** Imperative ref to the AIMenu — used to toggle on ⌘J. */
    aiMenuRef: React.RefObject<AIMenuHandle | null>;
    /** From `useMarkResetOnNavigation`. Scheduled (microtask) on Enter. */
    clearMarksAtCaret: () => void;
}

export interface UseEditorKeyboardShortcutsReturn {
    handleKeyDownCapture: (e: React.KeyboardEvent) => void;
}

export function useEditorKeyboardShortcuts({
    editor,
    readOnly,
    enableAI,
    markNextPasteAsPlain,
    setFindReplaceOpen,
    aiMenuRef,
    clearMarksAtCaret,
}: UseEditorKeyboardShortcutsConfig): UseEditorKeyboardShortcutsReturn {
    const handleKeyDownCapture = useCallback(
        (e: React.KeyboardEvent) => {
            // ⌘⇧V (Mac) / Ctrl⇧V (Win/Linux): mark the next paste as
            // plain-text only. We can't act on the paste here — the paste
            // event hasn't fired yet — so we tell the clipboard hook to
            // flag the next paste, and `handlePaste` consumes it.
            if (!readOnly && (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'v') {
                markNextPasteAsPlain();
                // Don't preventDefault — let the browser fire the paste event.
                return;
            }

            // ⌘F / Ctrl F: open Find & Replace. Stop the browser's native
            // find dialog (which doesn't work against contenteditable text).
            if (!readOnly && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                e.stopPropagation();
                setFindReplaceOpen(true);
                return;
            }

            // ⌘J / Ctrl J: toggle the AI menu. Gated by `enableAI` so the
            // shortcut is a no-op when AI is disabled by the visibility
            // flag — the AIToolbarButton in PlateToolbar/FloatingToolbar
            // is also hidden, so there's no surface to invoke it at all.
            if (enableAI && !readOnly && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
                e.preventDefault();
                e.stopPropagation();
                aiMenuRef.current?.toggle();
                return;
            }

            // Tab inside a table cell or list item indents in-place. Otherwise
            // bubble so the browser handles focus cycling normally (e.g.,
            // tabbing out of the editor to the next focusable element).
            if (e.key === 'Tab') {
                const ed: any = editor;
                const block = ed?.api?.block?.({ block: true });
                const blockNode = block?.[0];
                let insideCell = false;
                try {
                    const above = ed?.api?.above?.({
                        match: (n: any) =>
                            n?.type === 'td' || n?.type === 'th' || n?.type === 'table_cell' || n?.type === 'table_cell_header',
                    });
                    insideCell = Boolean(above);
                } catch {
                    /* api.above shape may vary */
                }
                const inList = Boolean(blockNode?.listStyleType);
                if (!insideCell && !inList) return;
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    (plateOutdent as any)(ed);
                } else {
                    (plateIndent as any)(ed);
                }
                return;
            }

            // Enter (without Shift) → clear inline marks on the next microtask.
            // Deferred so Plate's native Enter handling runs first and creates
            // the new block; we only strip the active marks afterwards. With
            // Shift+Enter (soft break), marks stay alive — that's the
            // "continue typing in the same style on the same paragraph"
            // intent users expect.
            if (e.key === 'Enter' && !e.shiftKey) {
                Promise.resolve().then(clearMarksAtCaret);
            }
        },
        [editor, clearMarksAtCaret, readOnly, enableAI, markNextPasteAsPlain, setFindReplaceOpen, aiMenuRef],
    );

    return { handleKeyDownCapture };
}
